import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { recommendationAssignKey } from "./assignRecommendations"
import { fetchRecommendations, toAiRecommendations } from "./recommendationsApi"
import type {
  AiDismissedEntry,
  AiPlanTarget,
  AiRecommendation,
  AiSourcesTab,
  AiSubject,
  AiSubjectResult,
} from "./aiLessonPlanningTypes"

export const PICKS_PER_SUBJECT = 3

const SUBJECT_ORDER: AiSubject[] = ["math", "english"]

const EMPTY_RESULT: AiSubjectResult = {
  status: "idle",
  items: [],
  executedAt: null,
  error: null,
}

interface StagedState {
  dismissed: AiDismissedEntry[]
}

const EMPTY_STAGED: StagedState = { dismissed: [] }

function storageKey(sessionKey: string | null, studentKey: string | null) {
  return `aiLessonPlanning:${sessionKey || "-"}:${studentKey || "-"}`
}

function readStaged(key: string): StagedState {
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return EMPTY_STAGED
    const parsed = JSON.parse(raw)
    return {
      dismissed: Array.isArray(parsed.dismissed) ? parsed.dismissed : [],
    }
  } catch (error) {
    return EMPTY_STAGED
  }
}

function subjectFromEnrolmentSubject(subject: any): AiSubject | null {
  const id = Number(subject?.fk_sub_id ?? subject?.subject_id)
  const name = String(subject?.subject_name || "").toLowerCase()
  if (id === 1 || name.includes("english")) return "english"
  if (id === 2 || name.includes("math")) return "math"
  return null
}

export function getEnrolledAiSubjects(
  sessionStudentData: any,
  studentKey: string | null
): AiSubject[] {
  const enrolments: any[] =
    sessionStudentData?.session?.tbl_session_time?.tbl_student_enrolment || []
  const ownEnrolments = enrolments.filter(
    enrolment => enrolment?.fk_stu_key == null || enrolment.fk_stu_key == studentKey
  )
  const found = new Set<AiSubject>()
  for (const enrolment of ownEnrolments.length ? ownEnrolments : enrolments) {
    for (const subject of enrolment?.tbl_enrolment_subject || []) {
      const mapped = subjectFromEnrolmentSubject(subject)
      if (mapped) found.add(mapped)
    }
  }
  return SUBJECT_ORDER.filter(subject => found.has(subject))
}

interface UseAiLessonPlanningArgs {
  studentKey: string | null
  sessionKey: string | null
  subjects: AiSubject[]
  enabled: boolean
  lessonAssignedKeys: string[]
  homeworkAssignedKeys: string[]
  onAssign: (items: AiRecommendation[], target: AiPlanTarget) => Promise<void>
}

export function useAiLessonPlanning({
  studentKey,
  sessionKey,
  enabled,
  lessonAssignedKeys,
  homeworkAssignedKeys,
  onAssign,
}: UseAiLessonPlanningArgs) {
  const [tab, setTab] = useState<AiSourcesTab>("ai")
  const [results, setResults] = useState<Record<AiSubject, AiSubjectResult>>({
    math: EMPTY_RESULT,
    english: EMPTY_RESULT,
  })
  const key = storageKey(sessionKey, studentKey)
  const [stagedBox, setStagedBox] = useState(() => ({
    key,
    data: readStaged(key),
  }))
  if (stagedBox.key !== key) {
    setStagedBox({ key, data: readStaged(key) })
  }
  const staged = stagedBox.key === key ? stagedBox.data : EMPTY_STAGED
  const setStaged = useCallback(
    (update: (prev: StagedState) => StagedState) => {
      setStagedBox(prev => ({ key: prev.key, data: update(prev.data) }))
    },
    []
  )
  const [assigningIds, setAssigningIds] = useState<string[]>([])
  const abortRef = useRef<AbortController | null>(null)
  const onAssignRef = useRef(onAssign)
  onAssignRef.current = onAssign
  const subjectsKey = SUBJECT_ORDER.join(",")
  const requestedSubjects = useMemo(
    () => subjectsKey.split(",").filter(Boolean) as AiSubject[],
    [subjectsKey]
  )

  useEffect(() => {
    try {
      sessionStorage.setItem(stagedBox.key, JSON.stringify(stagedBox.data))
    } catch (error) {
      // sessionStorage can be unavailable (private mode / quota); state still works in-memory
    }
  }, [stagedBox])

  const load = useCallback(() => {
    if (!enabled || !studentKey) return

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setResults(prev => {
      const next = { ...prev }
      for (const subject of requestedSubjects) {
        next[subject] = { ...prev[subject], status: "loading", error: null }
      }
      return next
    })

    requestedSubjects.forEach(subject => {
      fetchRecommendations(studentKey, subject, controller.signal)
        .then(data => {
          if (controller.signal.aborted) return
          setResults(prev => ({
            ...prev,
            [subject]: {
              status: "success",
              items: toAiRecommendations(subject, data),
              executedAt: data.executedAt ?? null,
              error: null,
            },
          }))
        })
        .catch((error: any) => {
          if (controller.signal.aborted || error?.name === "AbortError") return
          setResults(prev => ({
            ...prev,
            [subject]: {
              ...prev[subject],
              status: "error",
              error: error?.message || "Could not load recommendations",
            },
          }))
        })
    })
  }, [enabled, studentKey, subjectsKey, requestedSubjects])

  useEffect(() => {
    setResults({ math: EMPTY_RESULT, english: EMPTY_RESULT })
    load()
    return () => abortRef.current?.abort()
  }, [load])

  const allItems = useMemo(
    () => requestedSubjects.flatMap(subject => results[subject].items),
    [results, subjectsKey]
  )

  const byId = useMemo(() => {
    const map: Record<string, AiRecommendation> = {}
    for (const item of allItems) map[item.id] = item
    return map
  }, [allItems])

  const assign = useCallback(async (ids: string[], target: AiPlanTarget) => {
    const items = ids.map(id => byId[id]).filter(Boolean)
    if (items.length === 0) return
    setAssigningIds(prev => prev.concat(ids))
    try {
      await onAssignRef.current(items, target)
    } finally {
      setAssigningIds(prev => prev.filter(id => ids.indexOf(id) < 0))
    }
  }, [byId])

  const dismiss = useCallback((id: string, reason: string) => {
    setStaged(prev => ({
      ...prev,
      dismissed: prev.dismissed
        .filter(entry => entry.id !== id)
        .concat([{ id, reason }]),
    }))
  }, [setStaged])

  const undoDismiss = useCallback((id: string) => {
    setStaged(prev => ({
      ...prev,
      dismissed: prev.dismissed.filter(entry => entry.id !== id),
    }))
  }, [setStaged])

  const derived = useMemo(() => {
    const assigned = new Set([...lessonAssignedKeys, ...homeworkAssignedKeys])
    const dismissedIds = new Set(staged.dismissed.map(entry => entry.id))
    const isAssigned = (item: AiRecommendation) => {
      const key = recommendationAssignKey(item.sourceId)
      return key != null && assigned.has(key)
    }
    const available = allItems.filter(
      item => !isAssigned(item) && !dismissedIds.has(item.id)
    )
    const availableBySubject: Record<AiSubject, AiRecommendation[]> = {
      math: available.filter(item => item.subject === "math"),
      english: available.filter(item => item.subject === "english"),
    }

    const topPicks: AiRecommendation[] = []
    for (let index = 0; index < PICKS_PER_SUBJECT; index++) {
      for (const subject of requestedSubjects) {
        const item = availableBySubject[subject][index]
        if (item) topPicks.push(item)
      }
    }
    const topIds = new Set(topPicks.map(item => item.id))
    const nowIds = new Set(
      requestedSubjects
        .map(subject => availableBySubject[subject][0]?.id)
        .filter(Boolean) as string[]
    )

    return {
      available,
      availableBySubject,
      topPicks,
      nowIds,
      rest: available.filter(item => !topIds.has(item.id)),
      lessonCount: allItems.filter(item => {
        const key = recommendationAssignKey(item.sourceId)
        return key != null && lessonAssignedKeys.indexOf(key) >= 0
      }).length,
      homeworkCount: allItems.filter(item => {
        const key = recommendationAssignKey(item.sourceId)
        return key != null && homeworkAssignedKeys.indexOf(key) >= 0
      }).length,
      dismissedItems: staged.dismissed
        .filter(entry => byId[entry.id])
        .map(entry => ({ item: byId[entry.id], reason: entry.reason })),
    }
  }, [allItems, lessonAssignedKeys, homeworkAssignedKeys, staged, subjectsKey])

  const subjectStates = requestedSubjects.map(subject => ({
    subject,
    ...results[subject],
  }))

  return {
    ready: enabled && !!studentKey,
    tab,
    setTab,
    subjects: requestedSubjects,
    subjectStates,
    isLoading: subjectStates.some(state => state.status === "loading"),
    refresh: load,
    byId,
    assigningIds,
    assign,
    dismiss,
    undoDismiss,
    ...derived,
  }
}

export type AiLessonPlanningController = ReturnType<typeof useAiLessonPlanning>
